import * as B from '@blueprintjs/core';
import shpwrite from '@mapbox/shp-write';
import {VirtualItem} from '@tanstack/react-virtual';
import classnames from 'classnames';
import {ExportToCsv} from 'export-to-csv';
import * as I from 'immutable';
import React from 'react';
import {Link} from 'react-router-dom';
import {ColumnInstance, Row, TableInstance} from 'react-table';

import {useAlertPolicies} from 'app/components/Alerts/AlertPolicyProvider';
import {isDefaultUpstreamVegetationDropAlertPolicy} from 'app/components/Alerts/alertPolicyUtils';
import {DEFAULT_UPSTREAM_VEG_ALERT_POLICY} from 'app/components/Alerts/AlertsPoliciesPanel';
import LookoutIcon from 'app/components/Alerts/LookoutIcon';
import {PortfolioLayersSelector} from 'app/components/Library/PortfolioLayersSelector';
import List from 'app/components/List';
import {BulkOrderImageryModal} from 'app/components/OrderImageryModal/BulkOrderImageryModal';
import {AssigneeMenu} from 'app/components/PropertyEditors/AssigneeMenu';
import {DueDateMenu} from 'app/components/PropertyEditors/DueDateMenu';
import {AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser, getAreaUnit} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import EditTagsMenu from 'app/pages/MonitorProjectView/EditTagsMenu';
import {useChangeSelection} from 'app/pages/ProjectDashboardSwitch';
import {CombinePropertiesModal} from 'app/pages/PropertyOverview/CombinePropertiesModal';
import {GroupLocationsModal} from 'app/pages/PropertyOverview/GroupLocationsModal';
import PropertyOverviewFiltersMenu from 'app/pages/PropertyOverview/PropertyOverviewFiltersMenu';
import {
  getIsSomeSelected,
  getIsSubRow,
  getSelectedFeatures,
  getSelectedPropertyRows,
} from 'app/pages/PropertyOverview/PropertyTableSelection';
import {ImageDetails, ImageryStatus} from 'app/pages/PropertyOverview/PropertyTableTypes';
import {UngroupLocationsModal} from 'app/pages/PropertyOverview/UngroupLocationsModal';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import featureFlags, {useUserInfo} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as hookUtils from 'app/utils/hookUtils';
import {BULK_ORDER_LAYER_KEYS} from 'app/utils/layers';
import {
  getEnrolledLayerKeysForFeatureCollection,
  getLayer,
  parseLayerKey,
} from 'app/utils/layerUtils';
import {paysAtCheckout} from 'app/utils/organizationUtils';
import * as routeUtils from 'app/utils/routeUtils';
import * as tagUtils from 'app/utils/tagUtils';
import * as userUtils from 'app/utils/userUtils';

import {SearchBoxFilter} from './ColumnFilters';
import MovePropertiesMenu from './MovePropertiesMenu';
import cs from './PropertyOverview.module.scss';
import {
  ColumnSettingsContent,
  OrderedVisibleColumnConfiguration,
  PropertyTableRow,
} from './PropertyTableColumnSettings';
import {VEG_DROP_PLACEHOLDER_MENU_ID, getAlertListItems, menuIdForAlertPolicy} from './utils';

import {NoteCountsByFeatureId} from '.';

/**
 * Input element wrapper to handle indeterminate state.
 */
export const InputWrapper: React.FunctionComponent<
  React.PropsWithChildren<
    {
      state: 'checked' | 'indeterminate' | 'unchecked';
    } & React.HTMLProps<HTMLInputElement>
  >
> = ({state, ...inputProps}) => {
  const ref = React.useRef<HTMLInputElement>(null);

  // React doesn't have a declarative prop for the indeterminate state, so we
  // need to set it ourselves when some but not all rows are selected.
  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === 'indeterminate';
    }
  }, [state]);

  return <input ref={ref} type="checkbox" checked={state === 'checked'} {...inputProps} />;
};

const BulkOrderDropdown: React.FunctionComponent<
  React.PropsWithChildren<{
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    selectedFeatures: ApiFeature[];
    project: ApiProject;
  }>
> = ({featureCollection, selectedFeatures, project}) => {
  const [organization] = useUserInfo();
  const bulkOrderLayerKeys = Array.from(BULK_ORDER_LAYER_KEYS);
  const enrolledLayerKeys = getEnrolledLayerKeysForFeatureCollection(featureCollection);
  const filteredBulkOrderLayerKeys = bulkOrderLayerKeys.filter(
    (carbonKey) =>
      !!enrolledLayerKeys.find((enrolledKey) => {
        const {sourceId: enrolledSource} = parseLayerKey(enrolledKey);
        const {sourceId: packageSource} = parseLayerKey(carbonKey);
        return enrolledSource === packageSource;
      })
  );
  const [bulkOrderLayerKey, setBulkOrderLayerKey] = React.useState<string | null>(null);
  const bulkOrderLayerDefinitions = filteredBulkOrderLayerKeys.map((key) => getLayer(key));
  const featureCollectionJS = React.useMemo(
    () => featureCollection.toJS() as ApiFeatureCollection,
    [featureCollection]
  );

  // Bulk orders bill a contract, and the server refuses them for an org that pays at checkout.
  if (!filteredBulkOrderLayerKeys.length || paysAtCheckout(organization)) {
    return null;
  }

  return (
    <>
      <B.Popover
        position={B.Position.BOTTOM_RIGHT}
        modifiers={{arrow: {enabled: false}}}
        content={
          <List
            className={cs.actionMenu}
            items={bulkOrderLayerDefinitions.map((layer) => {
              const text = layer.vendor.split(' ')[0] + ' ' + layer.display;
              return {
                id: layer.key,
                text,
              };
            })}
            onClickItem={({id: layerKey}) => {
              setBulkOrderLayerKey(layerKey);
            }}
          />
        }
      >
        <B.Button icon="shopping-cart" rightIcon="caret-down">
          Bulk Order
        </B.Button>
      </B.Popover>
      {bulkOrderLayerKey && (
        <BulkOrderImageryModal
          features={selectedFeatures}
          layerKey={bulkOrderLayerKey}
          isOpen={true}
          onClose={() => {
            setBulkOrderLayerKey(null);
          }}
          onSubmit={() => {
            setBulkOrderLayerKey(null);
          }}
          project={project}
          featureCollection={featureCollectionJS}
        />
      )}
    </>
  );
};

function renderImageDetails({date, sourceId}: ImageDetails) {
  const sourceDetails =
    sourceId === null
      ? null
      : featureUtils.getSourceDetails(sourceId as featureUtils.SourceDetailsId);

  return (
    <>
      {date}
      {sourceDetails?.resolution !== undefined && `\xa0(${sourceDetails.resolution}m)`}
    </>
  );
}

export const HighResImageryCell: React.FunctionComponent<
  React.PropsWithChildren<{
    cell: {value: ImageryStatus};
    isHiddenImageryShownAsAvailable: boolean;
    openPopoverForTest?: boolean;
    showImageryPurchaseDrawer: () => void;
  }>
> = ({cell, isHiddenImageryShownAsAvailable, openPopoverForTest, showImageryPurchaseDrawer}) => {
  const {available: availableDates, purchased: purchasedDates, hidden: hiddenDates} = cell.value;

  let availableCount = availableDates.length;

  if (isHiddenImageryShownAsAvailable) {
    availableCount += hiddenDates.length;
  }

  if (availableCount === 0 && purchasedDates.length === 0) {
    return null;
  }

  const target = (
    <div style={{textDecoration: 'underline', textDecorationStyle: 'dotted'}}>
      <div>
        {/* TODO(fiona): show fraction of properties ordered if multi-part */}
        {purchasedDates.length > 0 && purchasedDates[0].date}
        {purchasedDates.length > 1 && `, …`}
      </div>

      {/* We show hidden and available counts together here (if "hidden" is being shown), but they'll still
      be broken out separately in the popover.

      For visual clarity, we don't show the "available" count if we had ordered dates.
      */}
      {purchasedDates.length === 0 && availableCount > 0 && (
        <div style={{fontStyle: 'italic'}}>{availableCount} available</div>
      )}
    </div>
  );

  return (
    <>
      {/* along with usePortal, needed to keep the popover from rendering too low */}
      <div style={{position: 'relative'}}>
        <B.Popover
          interactionKind={B.PopoverInteractionKind.HOVER}
          position={B.Position.RIGHT}
          isOpen={openPopoverForTest}
          content={
            <div className={cs.imageryPopoverContent} onClick={(ev) => ev.stopPropagation()}>
              {purchasedDates.length > 0 && (
                <>
                  <h4 className={cs.imageryPopoverHeading}>Ordered</h4>

                  <ul className={cs.imageryDateList}>
                    {purchasedDates.map((details) => (
                      <li key={`${details.date}-${details.sourceId}`}>
                        {renderImageDetails(details)}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {availableDates.length > 0 && (
                <>
                  <h4 className={cs.imageryPopoverHeading}>Available</h4>

                  <ul className={cs.imageryDateList}>
                    {availableDates.map((details) => (
                      <li key={`${details.date}-${details.sourceId}`}>
                        {renderImageDetails(details)}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {hiddenDates.length > 0 && (
                <>
                  <h4 className={cs.imageryPopoverHeading}>Hidden</h4>

                  <ul className={cs.imageryDateList}>
                    {hiddenDates.map((details) => (
                      <li key={`${details.date}-${details.sourceId}`}>
                        {renderImageDetails(details)}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <B.Button
                intent="primary"
                onClick={(ev) => {
                  ev.stopPropagation();
                  showImageryPurchaseDrawer();
                }}
                style={{whiteSpace: 'nowrap'}}
              >
                Show Imagery
              </B.Button>
            </div>
          }
        >
          {target}
        </B.Popover>
      </div>
    </>
  );
};

const SORTABLE_FIELDS = ['name', 'assignee', 'area', 'reportingDueDate'];
export const TableControls: React.FunctionComponent<
  React.PropsWithChildren<{
    table: TableInstance<PropertyTableRow>;
    featureCollection: I.ImmutableOf<ApiFeatureCollection>;
    project: I.ImmutableOf<ApiProject>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    columnSettings: OrderedVisibleColumnConfiguration;
    onColumnSettingsChange: (settings: OrderedVisibleColumnConfiguration) => void;
    organizationUsers?: ApiOrganizationUser[];
    loggedInUserActions: LoggedInUserActions;
    additionalFeatureProperties: I.Set<string>;
  }>
> = ({
  table,
  featureCollection,
  project,
  columnSettings,
  organizationUsers,
  onColumnSettingsChange,
  loggedInUserActions,
  additionalFeatureProperties,
}) => {
  const [isSelectLayersModalOpen, setIsSelectLayersModalOpen] = React.useState<boolean>(false);
  const [organization, profile] = useUserInfo();
  const [isHiddenImageryShownAsAvailable, setIsHiddenImageryShownAsAvailable] =
    React.useState(false);
  // Exclude the `selection` and `imagery` columns from the list of sortable column options.
  const columns = table.columns.filter(
    (col) => (col.id && SORTABLE_FIELDS.includes(col.id)) || additionalFeatureProperties.has(col.id)
  );
  const nameColumn = columns.find((col) => col.id === 'name');

  let sortColumn = columns.find((col) => col.isSorted);
  if (!sortColumn) {
    // nameColumn will always be visible
    sortColumn = nameColumn;
  }

  return (
    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
      <B.Popover
        position={B.Position.BOTTOM_RIGHT}
        content={
          <ColumnSettingsContent
            additionalFeatureProperties={additionalFeatureProperties}
            saveColumnSettings={loggedInUserActions.setOrganizationPropertyOverviewColumnSettings}
            columnSettings={columnSettings}
            onColumnSettingsChange={onColumnSettingsChange}
          />
        }
      >
        <B.Button outlined icon="cog" />
      </B.Popover>
      {organization &&
        organizationUsers &&
        featureFlags.PORTFOLIO_MAP_VIEW(organization, profile) && (
          <PropertyOverviewFiltersMenu
            organization={organization}
            organizationUsers={organizationUsers}
            table={table}
            selectedFeatureCollection={featureCollection}
            isHiddenImageryShownAsAvailable={isHiddenImageryShownAsAvailable}
            setIsHiddenImageryShownAsAvailable={setIsHiddenImageryShownAsAvailable}
            additionalFeatureProperties={additionalFeatureProperties}
          />
        )}
      <TableSortMenu columns={columns} sortColumn={sortColumn!} />
      <SearchBoxFilter setFilter={table.setFilter} column={nameColumn!} />
      {profile.get('role') !== 'readonly' && (
        <>
          <B.Button
            icon={'layers'}
            text={'Select datasets'}
            onClick={() => setIsSelectLayersModalOpen(true)}
            minimal
          />

          <Link
            to={`/${routeUtils.makeUuidPrefix(organization.get('id'))}/admin/manageProperties/select/${routeUtils.makeUuidPrefix(project.get('id'))}`}
          >
            Add or update properties
          </Link>
        </>
      )}
      <PortfolioLayersSelector
        project={project}
        featureCollection={featureCollection}
        isOpen={isSelectLayersModalOpen}
        onClose={() => setIsSelectLayersModalOpen(false)}
      />
    </div>
  );
};

/**
 * Sort direction and column picker. `sortColumn` is the column currently sorted
 * by, or the one to fall back to when none is.
 */
export function TableSortMenu<D extends object = PropertyTableRow>({
  columns,
  sortColumn,
}: {
  columns: ColumnInstance<D>[];
  sortColumn: ColumnInstance<D>;
}) {
  const {id: sortColumnId, isSortedDesc} = sortColumn;

  return (
    <B.Popover
      position={B.Position.BOTTOM_LEFT}
      content={
        <B.Menu>
          <B.MenuItem
            id="asc"
            type="direction"
            text={'Ascending'}
            icon={isSortedDesc ? 'blank' : 'small-tick'}
            onClick={() => sortColumn.toggleSortBy(false, false)}
          />
          <B.MenuItem
            id="desc"
            type="direction"
            text={'Descending'}
            icon={isSortedDesc ? 'small-tick' : 'blank'}
            onClick={() => sortColumn.toggleSortBy(true, false)}
          />

          <B.MenuDivider />

          {columns.map((col) => (
            <B.MenuItem
              key={col.id}
              id={col.id}
              type="column"
              text={col.Header?.toString()}
              icon={col.id === sortColumnId ? 'small-tick' : 'blank'}
              onClick={() => col.toggleSortBy(!!isSortedDesc, false)}
            />
          ))}
        </B.Menu>
      }
    >
      <B.Button outlined icon={isSortedDesc ? 'sort-desc' : 'sort-asc'} />
    </B.Popover>
  );
}

export function TableCellHeader<D extends object = PropertyTableRow>({
  column,
  table,
}: React.PropsWithChildren<{
  column: ColumnInstance<D>;
  table: TableInstance<D>;
}>) {
  const filterIsActive = !!table.state.filters.find((f) => f.id === column.id);
  return (
    <th key={column.id} className={classnames(column.className, cs.headerCell)}>
      <h3>
        {column.render('Header')}
        {column.loading && <MisleadingHeaderProgressBar />}
      </h3>

      {column.filter && (
        <div className={classnames(cs.filterWrapper, {[cs.activeFilter]: filterIsActive})}>
          {column.render('Filter')}
        </div>
      )}
    </th>
  );
}

export function TableHeader<D extends object = PropertyTableRow>({
  table,
  gridTemplateColumns,
}: React.PropsWithChildren<{
  table: TableInstance<D>;
  gridTemplateColumns?: string;
}>) {
  return (
    <tr className={cs.columnHeaders} style={{gridTemplateColumns}}>
      {table.columns.map((column) => (
        <TableCellHeader column={column} key={column.id} table={table} />
      ))}
    </tr>
  );
}

/**
 * Makes a progress bar that animates from 0 -> 0.8, then sits and spins until
 * it's unmounted.
 *
 * Used to show that we are loading a column's values, even if we're shady about
 * the actual amount of progress being made.
 */
const MisleadingHeaderProgressBar: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const [value, setValue] = React.useState(0);

  // The progress bar has a CSS transition that will smoothly animate to this
  // value, but for that to work we need to render with a value of 0 first.
  React.useEffect(() => {
    setValue(0.8);
  }, []);

  return <B.ProgressBar value={value} className={cs.columnHeaderLoading} />;
};

export const PropertyRow: React.FunctionComponent<
  React.PropsWithChildren<{
    data: {
      organizationId: string;
      projectId: string;
      featureCollectionId: number;
      allowSelection: boolean;
      table: TableInstance<PropertyTableRow>;
      onRowFocus: (row: Row<PropertyTableRow>) => void;
      onRowBlur: (row: Row<PropertyTableRow>) => void;
      gridTemplateColumns?: string;
    };
    virtualRow: VirtualItem<Element>;
  }>
> = ({
  data: {
    table,
    organizationId,
    projectId,
    allowSelection,
    onRowFocus,
    onRowBlur,
    gridTemplateColumns,
  },
  virtualRow,
}) => {
  const row = table.rows[virtualRow.index];
  table.prepareRow(row);

  const elRef = React.useRef<HTMLDivElement>(null);
  const changeSelection = useChangeSelection();

  const navigateToFeature = React.useCallback(() => {
    changeSelection({
      organizationId,
      projectId,
      featureIds: row.original.features.map((f) => f!.getIn(['properties', 'lensId'])!).toSet(),
    });
  }, [changeSelection, organizationId, projectId, row.original]);

  const onKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (ev.key === 'Enter') {
        navigateToFeature();
      } else if (ev.key === ' ') {
        if (allowSelection) {
          row.toggleRowSelected();
        }
        // Keeps us from scrolling down (in all cases, for consistency)
        ev.preventDefault();
      } else if (ev.key === 'ArrowDown') {
        if (elRef.current && elRef.current.nextElementSibling) {
          (elRef.current.nextElementSibling as HTMLElement).focus();
          ev.preventDefault();
        }
      } else if (ev.key === 'ArrowUp') {
        if (elRef.current && elRef.current.previousElementSibling) {
          (elRef.current.previousElementSibling as HTMLElement).focus();
          ev.preventDefault();
        }
      }
    },
    [navigateToFeature, row, allowSelection]
  );

  const isSubRow = getIsSubRow(row);

  // console will get mad it we destructure the key so pull it out
  const rowProps = row.getRowProps();
  const {key, ...restRowProps} = rowProps;

  return (
    <tr
      key={key}
      {...restRowProps}
      data-id={row.id}
      // cs.row is important because that's what SearchBoxFilter looks for to
      // focus on when the down arrow is pressed.
      className={classnames(cs.row, {
        [cs.selected]: row.isSelected,
        [cs.expanded]: row.isExpanded,
        [cs.subRow]: isSubRow,
      })}
      // Giving ourselves a tabIndex lets the user use keyboard navigation
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={() => onRowFocus(row)}
      onBlur={() => onRowBlur(row)}
      style={{
        position: 'absolute',
        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
        width: '100%',
        ...(gridTemplateColumns ? {gridTemplateColumns} : {}),
      }}
    >
      {row.cells.map((cell) => {
        return (
          // key is provided by getCellProps

          <td
            {...cell.getCellProps()}
            key={cell.getCellProps().key}
            className={classnames(cell.column.className, {
              [cs.cellSelectSubRow]: isSubRow && cell.column.id === 'selection',
              [cs.cellTitleSubRow]: isSubRow && cell.column.id === 'name',
            })}
          >
            {cell.render('Cell')}
          </td>
        );
      })}
    </tr>
  );
};

export const SelectedRowActions: React.FunctionComponent<
  React.PropsWithChildren<{
    project: I.ImmutableOf<ApiProject>;
    role: ApiOrganizationUser['role'];
    organizationUsers: ApiOrganizationUser[];
    organization: I.ImmutableOf<ApiOrganization>;
    userNoteCountsByFeatureId: hookUtils.StatusMaybe<NoteCountsByFeatureId>;
    alertNoteCountsByFeatureId: hookUtils.StatusMaybe<ApiAlertCountsByFeatureId>;
    year: number;
    rows: Row<PropertyTableRow>[];
    clearSelection: () => void;
    updateFeature: FeaturesActions['updateFeature'];
    batchPatchFeature: FeaturesActions['batchPatchFeature'];
    batchArchiveFeatures: FeaturesActions['batchArchiveFeatures'];
    moveFeatures: FeaturesActions['moveFeatures'];
    refreshFeatures: () => void;
    selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
    openCustomizeTagsModal: () => void;
    setIsUpdatingFeatures: React.Dispatch<React.SetStateAction<boolean>>;
    openTaskImageryModal?: () => void;
  }>
> = ({
  project,
  role,
  organizationUsers,
  organization,
  userNoteCountsByFeatureId,
  alertNoteCountsByFeatureId,
  year,
  rows,
  updateFeature,
  batchPatchFeature,
  batchArchiveFeatures,
  moveFeatures,
  clearSelection,
  refreshFeatures,
  selectedFeatureCollection,
  openCustomizeTagsModal,
  setIsUpdatingFeatures,
  openTaskImageryModal,
}) => {
  const areaUnit = getAreaUnit(organization);
  const [_, profile] = useUserInfo();

  const selectedPropertyRows = getSelectedPropertyRows(rows);

  const selectedFeatures = getSelectedFeatures(rows);
  const selectedFeatureNameSet = React.useMemo(
    () =>
      selectedFeatures
        .map((f) => (f!.getIn(['properties', 'name'] as any) as string) || '')
        .toSet(),
    [selectedFeatures]
  );

  const selectedArea = React.useMemo(
    () =>
      conversionUtils.numberWithCommas(
        featureUtils.featureM2ToArea(
          selectedFeatures.reduce((n, f) => n! + featureUtils.getFeatureAreaM2(f!), 0),
          {unit: areaUnit}
        )
      ),
    [selectedFeatures, areaUnit]
  );

  const {
    EXPORT_ID,
    EXPORT_NAME,
    EXPORT_LOCATION_NAME,
    EXPORT_TAGS,
    EXPORT_AREA,
    EXPORT_NOTES,
    EXPORT_ALERTS,
    EXPORT_ASSIGNEE,
    EXPORT_DUE_DATE,
  } = featureUtils.FeatureAttributeExport;
  const exportCSV = () => {
    const tagSettings = tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE);

    const OMITTED_ATTRIBUTES = [
      // Omit system attributes
      ...featureUtils.FeatureAttributes.internalIdentifiers,
      ...featureUtils.FeatureAttributes.systemMetadata,
      ...featureUtils.FeatureAttributes.hiddenProperties,
      // Omit any attributes matching leading columns, if they happend to have been uploaded with them
      // We do this so that they don't clobber data in each row when assembling the data.
      EXPORT_ID,
      EXPORT_NAME,
      EXPORT_LOCATION_NAME,
      EXPORT_TAGS,
      EXPORT_AREA[areaUnit],
      EXPORT_ASSIGNEE,
      EXPORT_DUE_DATE,
    ];

    const pojoFeatures = selectedFeatures.toArray().map((f) => f.toJS());

    // Reduce selected feature's properties to a single, combined attribute list, omitting system attributes and attributes matching existing csv columns.
    const extraAttributes: string[] = Object.keys(
      featureUtils.moveFieldsToTop(
        pojoFeatures
          .map((f) => Object.keys(f.properties) || [])
          .reduce<Record<string, 1>>((keys: Record<string, 1>, n: string[]) => {
            n?.forEach((e) => (keys[e] = 1));
            return keys;
          }, {}),
        featureUtils.FeatureAttributes.identifiers
      )
    ).filter((e) => !OMITTED_ATTRIBUTES.includes(e));

    extraAttributes.splice(0, 3);

    const csvExporter = new ExportToCsv({
      showLabels: true,
      useBom: false,
      filename: `${project.get('name').replace(/\s+/g, '_')}_properties`,
      headers: [
        EXPORT_ID,
        EXPORT_NAME,
        EXPORT_LOCATION_NAME,
        EXPORT_TAGS,
        EXPORT_AREA[areaUnit],
        `${EXPORT_NOTES} ${year}`,
        EXPORT_ALERTS,
        EXPORT_ASSIGNEE,
        EXPORT_DUE_DATE,
        ...extraAttributes,
      ],
    });

    const data = pojoFeatures.map((f) => {
      const tagIds = tagUtils.getFeatureTagIds(f);
      const activeTags = tagSettings
        .filter((t) => tagIds.includes(t!.get('id')))
        .toList()
        .map((t) => t!.get('text').replace(/,/g, ''))
        .toJS()
        .join(',');
      const noteCounts =
        (userNoteCountsByFeatureId.status === 'some' &&
          userNoteCountsByFeatureId.value.getIn([f.id, year])) ||
        0;
      const alertCounts =
        (alertNoteCountsByFeatureId.status === 'some' &&
          alertNoteCountsByFeatureId.value[f.id]?.['all']) ||
        0;

      // Note: These objects are order sensitive, so attributes at the top will appear first in the row.
      return {
        [EXPORT_ID]: f.properties.lensId,
        [EXPORT_NAME]: f.properties.name,
        [EXPORT_LOCATION_NAME]: f.properties.multiFeaturePartName ?? '',
        [EXPORT_TAGS]: activeTags,
        [EXPORT_AREA[areaUnit]]: featureUtils.featureM2ToArea(featureUtils.getFeatureAreaM2(f), {
          unit: areaUnit,
        }),
        [`${EXPORT_NOTES} ${year}`]: noteCounts,
        [EXPORT_ALERTS]: alertCounts,
        [EXPORT_ASSIGNEE]: featureUtils
          .resolveAssignees(
            f.properties?.[CONSTANTS.APP_PROPERTIES_KEY]?.[CONSTANTS.APP_PROPERTY_ASSIGNEE_EMAILS],
            f.properties?.[CONSTANTS.APP_PROPERTIES_KEY]?.[CONSTANTS.APP_PROPERTY_ASSIGNEE_EMAIL]
          )
          .join('; '),
        [EXPORT_DUE_DATE]: featureUtils.getIn(
          f,
          [
            'properties',
            CONSTANTS.APP_PROPERTIES_KEY,
            CONSTANTS.APP_PROPERTY_REPORTING_DUE_DATE,
          ].join('.'),
          ''
        ),
        ...Object.fromEntries(
          extraAttributes.map((attribute) => [attribute, f.properties[attribute] ?? ''])
        ),
      };
    });

    csvExporter.generateCsv(data);
  };

  const exportShapefile = React.useCallback(() => {
    const fileName = `${project.get('name').replace(/\s+/g, '_')}_properties`;
    shpwrite.download(
      geoJsonUtils.featureCollection(
        selectedFeatures
          .map((f) => {
            const id = f!.getIn(['properties', 'lensId']);
            const featureProperties = f!.getIn(['properties']).toJS();
            const omittedProperties = [
              ...featureUtils.FeatureAttributes.hiddenProperties,
              ...featureUtils.FeatureAttributes.internalIdentifiers,
              ...featureUtils.FeatureAttributes.systemMetadata,
            ];
            const exportedProperties = featureUtils.moveFieldsToTop(
              Object.fromEntries(
                Object.entries(featureProperties).filter(([attribute]) => {
                  // Omit double underscored attributes. These are likely system metadata.
                  return !(
                    attribute.startsWith('__') ||
                    attribute.startsWith('_') ||
                    omittedProperties.includes(attribute)
                  );
                })
              ),
              featureUtils.FeatureAttributes.identifiers
            );

            // There is a bug in the shp-write library where when trying to export a FeatureCollection
            // containing Polygons and MultiPolygons, only the MultiPolygons are included in the
            // exported shapefile. https://github.com/mapbox/shp-write/issues/121
            // So we convert Polygons to one part MultiPolygons
            let geometry = f!.get('geometry').toJS();
            if (geometry.type === 'Polygon') {
              geometry = {...geometry, type: 'MultiPolygon', coordinates: [geometry.coordinates]};
            }

            return geoJsonUtils.feature(geometry, exportedProperties, {id});
          })
          .toArray()
      ),
      {
        filename: fileName,
        folder: fileName,
        types: {
          polygon: fileName,
        },
        compression: 'STORE',
        outputType: 'blob',
      }
    );
  }, [project, selectedFeatures]);

  const archiveSelectedFeatures = async () => {
    const multiple = selectedFeatures.size > 1;
    const message = `Are you sure you want to delete ${
      multiple ? 'these properties' : 'this property'
    }? ${
      multiple ? 'They' : 'It'
    } will no longer be visible in Lens, so we recommend saving any reports prior to deleting.`;

    if (confirm(message)) {
      setIsUpdatingFeatures(true);
      await batchArchiveFeatures({
        featureCollectionId: selectedFeatureCollection.get('id'),
        features: selectedFeatures,
      });
      recordEvent('Archived features', {
        featureCollectionId: selectedFeatureCollection.get('id'),
        featureCount: selectedFeatures.size,
      });
      setIsUpdatingFeatures(false);
    }
  };

  const onClickDeactivateAllAlerts = async (alertPolicies) => {
    //create deactivate patch for all selected features for all Upstream alerts
    const featurePatch = I.fromJS({
      properties: {
        [CONSTANTS.APP_PROPERTIES_KEY]: {
          [CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY]: false,
        },
      },
    });
    //bulk unenroll any custom alerts with at least one enrolled feature
    await Promise.all(
      alertPolicies.map((alertPolicy) =>
        unenrollFeaturesInAlertPolicy(
          alertPolicy,
          selectedFeatures.toJS().map((feature) => feature.id)
        )
      )
    );

    //run feature patch for Upstream alerts
    batchPatchFeature({
      featureCollectionId: selectedFeatureCollection.get('id'),
      features: selectedFeatures,
      changes: I.fromJS(featurePatch),
    });
  };

  const onClickSingleUpstreamAlert = async (id) => {
    let featurePatch: I.MergesInto<I.ImmutableOf<ApiFeature>> = {};
    // if we select a single Upstream alert, enroll selected features in that alert
    // unless they're all already enrolled, in which case unenroll them
    if (enrollmentStatusByKey[id] !== 'all') {
      featurePatch = featureUtils.makeAlertEnrollmentAppPropertyPatch(id, true);
    } else {
      featurePatch = featureUtils.makeAlertEnrollmentAppPropertyPatch(id, false);
    }
    batchPatchFeature({
      featureCollectionId: selectedFeatureCollection.get('id'),
      features: selectedFeatures,
      changes: I.fromJS(featurePatch),
    });
  };

  const onClickSingleCustomAlert = (selectedCustomAlert: AlertPolicy) => {
    if (
      featureUtils.enrollmentStatusForCustomAlert(selectedFeatures, selectedCustomAlert) !== 'all'
    ) {
      enrollFeaturesInAlertPolicy(
        selectedCustomAlert,
        selectedFeatures.toJS().map((feature) => feature.id)
      );
    } else {
      unenrollFeaturesInAlertPolicy(
        selectedCustomAlert,
        selectedFeatures.toJS().map((feature) => feature.id)
      );
    }
  };

  const {
    alertPolicies,
    actions: {enrollFeaturesInAlertPolicy, unenrollFeaturesInAlertPolicy, createAlertPolicy},
  } = useAlertPolicies();

  const isOwnerOrAdminUser = userUtils.isOwnerOrAdmin(role);
  const allowSelection = role !== 'readonly';
  const defaultVegetationDropAlertPolicy = alertPolicies.find((alertPolicy) =>
    isDefaultUpstreamVegetationDropAlertPolicy(alertPolicy)
  );
  const vegetationDropEnrollment = defaultVegetationDropAlertPolicy
    ? featureUtils.enrollmentStatusForCustomAlert(
        selectedFeatures,
        defaultVegetationDropAlertPolicy
      )
    : 'none';
  const ownershipChangeEnrollment = featureUtils.enrollmentStatusForUpstreamAlert(
    selectedFeatures,
    CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY
  );
  const parcelAreaChangeEnrollment = featureUtils.enrollmentStatusForUpstreamAlert(
    selectedFeatures,
    CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY
  );

  const enrollmentStatusByKey = {
    [CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY]: ownershipChangeEnrollment,
    [CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY]: parcelAreaChangeEnrollment,
  };

  const projectJS = React.useMemo(() => project.toJS(), [project]);
  const selectedFeaturesJS = React.useMemo(
    () => selectedFeatures.toJS() as ApiFeature[],
    [selectedFeatures]
  );

  const [isCombineFeaturesModalOpen, setIsCombineFeaturesModalOpen] = React.useState(false);
  const [isGroupLocationsModalOpen, setIsGroupLocationsModalOpen] = React.useState(false);
  const [isUngroupLocationsModalOpen, setIsUngroupLocationsModalOpen] = React.useState(false);

  const hasMultiLocationPropertySelected = selectedPropertyRows.some((row) => {
    const features = row.original.features;
    return !!features && features.size > 1;
  });
  const canGroupLocations =
    allowSelection && selectedFeatures.size >= 2 && selectedFeatureNameSet.size >= 2;
  const canUngroupLocations =
    allowSelection &&
    (hasMultiLocationPropertySelected ||
      (selectedFeatures.size >= 2 && selectedFeatureNameSet.size < selectedFeatures.size));

  return (
    <div className={cs.selectedRowActions}>
      <div className={cs.selectedRowSummary}>
        <strong>
          {`${selectedPropertyRows.some((row) => getIsSomeSelected(row)) ? 'Locations from ' : ''}${
            selectedPropertyRows.length === 1
              ? '1 property selected'
              : `${selectedPropertyRows.length} properties selected`
          }`}
        </strong>{' '}
        ({selectedArea} {areaUnit === 'areaHectare' ? 'hectares' : 'acres'}) –{' '}
        <a
          onClick={(ev) => {
            ev.preventDefault();
            clearSelection();
          }}
        >
          clear selection
        </a>
      </div>

      <div className={cs.selectedRowButtons}>
        <EditTagsMenu
          featureCollectionId={selectedFeatureCollection.get('id')}
          tagSettings={tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE)}
          tagStatusMaps={tagUtils.getFeaturesTagStatusMaps(selectedFeatures)}
          onChange={(tagStatusMap) =>
            batchPatchFeature({
              featureCollectionId: selectedFeatureCollection.get('id'),
              features: selectedFeatures,
              changes: featureUtils.tagStatusMapToPropertiesChanges(tagStatusMap),
            })
          }
          openCustomizeTagsModal={
            userUtils.atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile)
              ? openCustomizeTagsModal
              : null
          }
          kind={tagUtils.TAG_KIND.FEATURE}
        >
          <B.Button icon="tag" rightIcon="caret-down" disabled={!allowSelection}>
            Tags
          </B.Button>
        </EditTagsMenu>

        {isOwnerOrAdminUser && (
          <>
            <BulkOrderDropdown
              featureCollection={selectedFeatureCollection}
              selectedFeatures={selectedFeaturesJS}
              project={projectJS}
            />
          </>
        )}

        <B.Popover
          position={B.Position.BOTTOM_RIGHT}
          modifiers={{arrow: {enabled: false}}}
          content={
            <List
              className={cs.actionMenu}
              items={[
                {
                  id: 'deactivate-alerts',
                  text: 'Unenroll selected properties',
                },
                {id: 'sep', text: '', isSeparator: true},
                ...getAlertListItems(
                  vegetationDropEnrollment,
                  ownershipChangeEnrollment,
                  parcelAreaChangeEnrollment,
                  selectedFeatureCollection,
                  alertPolicies,
                  selectedFeatures,
                  featureFlags.PARCEL_CHANGE_ALERTS(organization)
                ),
              ]}
              onClickItem={async ({id}) => {
                const hasDefaultVegDropAlertPolicy = alertPolicies.some(
                  (alertPolicy) =>
                    alertPolicy.name === CONSTANTS.ALERT_VEGETATION_DROP_DEFAULT_POLICY_NAME
                );
                if (id === VEG_DROP_PLACEHOLDER_MENU_ID && !hasDefaultVegDropAlertPolicy) {
                  const defaultVegDropAlertPolicy = await createAlertPolicy(
                    selectedFeatureCollection,
                    DEFAULT_UPSTREAM_VEG_ALERT_POLICY
                  );
                  onClickSingleCustomAlert({
                    ...(defaultVegDropAlertPolicy as AlertPolicy),
                    // onClickSingleCustomAlert handles the enrollment
                    enrollments: [],
                  });
                }
                const selectedCustomAlert = alertPolicies.find(
                  (alertPolicy) => menuIdForAlertPolicy(alertPolicy) === id
                );
                const alertsWithAnyEnrolledFeatures = alertPolicies.filter(
                  (alertPolicy) =>
                    featureUtils.enrollmentStatusForCustomAlert(selectedFeatures, alertPolicy) !==
                    'none'
                );
                if (
                  id === 'deactivate-alerts' &&
                  confirm('Are you sure you want to deactivate all alerts for selected properties?')
                ) {
                  await onClickDeactivateAllAlerts(alertsWithAnyEnrolledFeatures);
                } else if (featureUtils.isAlertEnrollmentKey(id)) {
                  await onClickSingleUpstreamAlert(id);
                } else if (selectedCustomAlert) {
                  await onClickSingleCustomAlert(selectedCustomAlert);
                }
              }}
            />
          }
        >
          <B.Button icon={<LookoutIcon />} rightIcon="caret-down" disabled={!allowSelection}>
            {'Lookouts'}
          </B.Button>
        </B.Popover>

        <AssigneeMenu
          featureCollection={selectedFeatureCollection}
          features={selectedFeatures}
          organizationUsers={organizationUsers}
          batchPatchFeature={batchPatchFeature}
          menuClassName={cs.actionMenu}
        >
          <B.Button icon="person" rightIcon="caret-down" disabled={!allowSelection}>
            Assignee
          </B.Button>
        </AssigneeMenu>

        <DueDateMenu
          featureCollection={selectedFeatureCollection}
          features={selectedFeatures}
          batchPatchFeature={batchPatchFeature}
        >
          <B.Button icon="calendar" rightIcon="caret-down" disabled={!allowSelection}>
            Due Date
          </B.Button>
        </DueDateMenu>

        {allowSelection && (
          <MovePropertiesMenu
            features={selectedFeatures}
            featureCollectionId={selectedFeatureCollection.get('id')}
            projectId={project.get('id')}
            moveFeatures={moveFeatures}
            setIsUpdatingFeatures={setIsUpdatingFeatures}
            organization={organization}
            alertPolicies={alertPolicies}
          >
            <B.Button icon="add-to-folder" rightIcon="caret-down" disabled={!allowSelection}>
              Move
            </B.Button>
          </MovePropertiesMenu>
        )}

        {isOwnerOrAdminUser && (
          <B.Button icon="trash" onClick={archiveSelectedFeatures}>
            Delete
          </B.Button>
        )}

        <B.Popover
          position={B.Position.BOTTOM_RIGHT}
          modifiers={{arrow: {enabled: false}}}
          captureDismiss={true}
          content={
            <B.Menu>
              <B.MenuItem text={`Export shapefile`} onClick={exportShapefile} />
              <B.MenuItem text="Export CSV" onClick={exportCSV} />
              {userUtils.atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile) && (
                <React.Fragment>
                  <B.MenuDivider />
                  <B.MenuItem
                    text="Merge properties"
                    onClick={() => setIsCombineFeaturesModalOpen(true)}
                  />
                  <B.MenuItem
                    text="Create multi-location property"
                    disabled={!canGroupLocations}
                    onClick={() => setIsGroupLocationsModalOpen(true)}
                  />
                  <B.MenuItem
                    text="Split multi-location property"
                    disabled={!canUngroupLocations}
                    onClick={() => setIsUngroupLocationsModalOpen(true)}
                  />
                  {openTaskImageryModal && (
                    <B.MenuItem text="Estimate tasking" onClick={openTaskImageryModal} />
                  )}
                </React.Fragment>
              )}
            </B.Menu>
          }
        >
          <B.Button icon="more" disabled={!allowSelection} />
        </B.Popover>
        <CombinePropertiesModal
          isOpen={isCombineFeaturesModalOpen}
          selectedFeatures={selectedFeaturesJS}
          onClose={() => setIsCombineFeaturesModalOpen(false)}
          refreshFeatures={refreshFeatures}
        />
        <GroupLocationsModal
          isOpen={isGroupLocationsModalOpen}
          onClose={() => setIsGroupLocationsModalOpen(false)}
          onDone={clearSelection}
          featureCollectionId={selectedFeatureCollection.get('id')}
          selectedFeatures={selectedFeatures}
          updateFeature={updateFeature}
          setIsUpdatingFeatures={setIsUpdatingFeatures}
        />
        <UngroupLocationsModal
          isOpen={isUngroupLocationsModalOpen}
          onClose={() => setIsUngroupLocationsModalOpen(false)}
          onDone={clearSelection}
          featureCollectionId={selectedFeatureCollection.get('id')}
          selectedFeatures={selectedFeatures}
          updateFeature={updateFeature}
          setIsUpdatingFeatures={setIsUpdatingFeatures}
        />
      </div>
    </div>
  );
};
